/** @file Poses the scene through poseAt (setup, animation or mix), drag previews before updatePose. */
import { POSE_LAYOUT } from '@pose2d/core/contracts/pose';
import type { Pose } from '@pose2d/core/types';
import { poseAt, type PoseAt } from '../../../playback/poseAt';
import { CHANNEL_OFFSET, type LocalChannel, type PreviewValues } from './channelOffset';
import type { Scene } from './createScene';

/** What to show (`currentPoseAt` of the stores, W3-30) plus previews by data bone index (R-E1-04, R-E1-05). */
export interface PoseInput {
  at: PoseAt;
  preview: ReadonlyMap<number, PreviewValues>;
}

/** Writes one bone's previewed channels into pose.local. */
function writeBone(pose: Pose, bone: number, values: PreviewValues): void {
  const base = bone * POSE_LAYOUT.LOCAL_STRIDE;
  for (const channel of Object.keys(values) as LocalChannel[]) {
    const v = values[channel];
    if (v !== undefined) pose.local[base + CHANNEL_OFFSET[channel]] = v;
  }
}

/** poseAt with the previews as its preview hook, so IK solves live: `pose.world` is ready to read. */
export function poseScene(scene: Scene, input: PoseInput): void {
  const writePreview = input.preview.size === 0 ? undefined : (pose: Pose): void => {
    for (const [bone, values] of input.preview) writeBone(pose, bone, values);
  };
  poseAt(scene.data, scene.pose, scene.scratch, input.at, writePreview);
}
